/**
 * Aliases tag parser for extracting contract alias mappings.
 *
 * Contracts can define alias-to-contract-ID mappings using:
 *   {{aliases config=CCOBK... registry=CCDBT... pages=CDJG...}}
 *   {{aliases {"config":"CCOBK...","registry":"CCDBT..."}}}
 *
 * These mappings allow includes to use friendly alias names instead of
 * full 56-character contract IDs:
 *   {{include contract=config func="logo"}}
 * Instead of:
 *   {{include contract=CCOBKFEZERN3SSZBFAZY5A6M2WPLVDLKPAWU7IEUQU35VMA6VKHXA62C func="logo"}}
 */
// Regex to match {{aliases ...}} tags
// Captures everything between {{aliases and }}
const ALIASES_TAG_REGEX = /\{\{aliases\s+([^}]+)\}\}/gi;
// Regex to parse individual key=value attributes
// Handles: key=value, key="value", key='value'
const ATTR_REGEX = /(\w+)=(?:"([^"]*)"|'([^']*)'|(\S+))/g;
/**
 * Parse content to find all alias definition tags.
 * Returns extracted alias mappings and content with alias tags removed.
 */
export function parseAliases(content) {
    const aliasTags = [];
    const aliases = {};
    ALIASES_TAG_REGEX.lastIndex = 0;
    let match;
    while ((match = ALIASES_TAG_REGEX.exec(content)) !== null) {
        const fullMatch = match[0];
        const attrsString = match[1];
        const startIndex = match.index;
        const endIndex = startIndex + fullMatch.length;
        if (!attrsString)
            continue;
        const trimmed = attrsString.trim();
        let mappings = {};
        // Try JSON format first: {{aliases {"config":"CCOBK..."}}}
        if (trimmed.startsWith("{")) {
            try {
                const parsed = JSON.parse(trimmed);
                if (typeof parsed === "object" && parsed !== null) {
                    mappings = parsed;
                }
            }
            catch {
                // Not valid JSON, try key=value format
                mappings = parseKeyValueAttributes(attrsString);
            }
        }
        else {
            // Key=value format: {{aliases config=CCOBK... registry=CCDBT...}}
            mappings = parseKeyValueAttributes(attrsString);
        }
        // Only add if we got any mappings
        if (Object.keys(mappings).length > 0) {
            aliasTags.push({
                original: fullMatch,
                startIndex,
                endIndex,
                mappings,
            });
            // Merge into combined mappings (later values override earlier ones)
            Object.assign(aliases, mappings);
        }
    }
    // Remove alias tags from content
    let processedContent = content;
    // Sort by startIndex descending to remove from end first
    const sortedTags = [...aliasTags].sort((a, b) => b.startIndex - a.startIndex);
    for (const tag of sortedTags) {
        processedContent =
            processedContent.slice(0, tag.startIndex) +
                processedContent.slice(tag.endIndex);
    }
    return { content: processedContent, aliases, aliasTags };
}
/**
 * Parse key=value attribute string into key-value pairs.
 */
function parseKeyValueAttributes(attrsString) {
    const attrs = {};
    ATTR_REGEX.lastIndex = 0;
    let match;
    while ((match = ATTR_REGEX.exec(attrsString)) !== null) {
        const key = match[1];
        if (!key)
            continue;
        // Value can be in match[2] (double quotes), match[3] (single quotes), or match[4] (unquoted)
        const value = match[2] ?? match[3] ?? match[4] ?? "";
        attrs[key] = value;
    }
    return attrs;
}
/**
 * Check if content contains any alias definition tags.
 */
export function hasAliasTags(content) {
    ALIASES_TAG_REGEX.lastIndex = 0;
    return ALIASES_TAG_REGEX.test(content);
}
/**
 * Resolve an alias to its contract ID.
 * Returns the original value if no alias mapping exists.
 */
export function resolveAlias(alias, aliases) {
    return aliases[alias] ?? alias;
}
//# sourceMappingURL=aliases.js.map